import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledLink from 'Components/StyledLink'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import type {
  NotificationParamShape,
  WebShopNotificationShape
} from 'Services/SalesManagement/Features/Notification/notificationSlice'
import useTypedSelector from 'Store'
import { ROWS_PER_PAGE_DEFAULT, SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import type { FC } from 'react'
import { t } from 'ttag'

const WebShopNotificationList: FC<{
  rows: WebShopNotificationShape[]
  loading: string
  params: NotificationParamShape
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
}> = ({ rows, loading, params, handleRequestSort, handleChangePage, count }) => {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/notifications/webshop`
  const user = useTypedSelector(state => state.user.user)
  const headCells: HeadCell[] = [
    {
      id: 'created',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    },
    {
      id: 'active_from',
      numeric: false,
      disablePadding: false,
      label: t`Active from`,
      sortable: true
    },
    {
      id: 'active_to',
      numeric: false,
      disablePadding: false,
      label: t`Active to`,
      sortable: true
    },
    {
      id: 'authority_name',
      numeric: false,
      disablePadding: false,
      label: t`Authority`,
      sortable: false
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: t`Type`,
      sortable: false
    },
    {
      id: 'content',
      numeric: false,
      disablePadding: false,
      label: t`Content`,
      sortable: false
    },
    ...(isSuperuser(user)
      ? [
          {
            id: 'author',
            numeric: false,
            disablePadding: false,
            label: t`Author`,
            sortable: false
          }
        ]
      : [])
  ]

  return (
    <WithLoader loading={loading === 'pending'} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={params.ordering}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow hover tabIndex={-1} key={row.id}>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created)}
                      component="th"
                      scope="row"
                    >
                      <StyledLink to={`${baseUrl}/${row.id}/`}>
                        {datetimeFormatter(row.created)}
                      </StyledLink>
                    </NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(row.active_from)}>
                      {datetimeFormatter(row.active_from)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(row.active_to)}>
                      {datetimeFormatter(row.active_to)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.authority_name)}>
                      {showValueOrHyphen(row.authority_name)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={row.type}>{row.type}</NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.content_fi)}>
                      {showValueOrHyphen(row.content_fi)}
                    </NoWrapTableCell>
                    {isSuperuser(user) && (
                      <NoWrapTableCell title={showValueOrHyphen(row.author_email)}>
                        {showValueOrHyphen(row.author_email)}
                      </NoWrapTableCell>
                    )}
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={count}
          page={params.page - 1}
          onPageChange={handleChangePage}
        />
      </Paper>
    </WithLoader>
  )
}

export { WebShopNotificationList }
